import { Button } from "@chakra-ui/react";
import {
  InternalFeatureFlagCustomerTable,
  InternalFeatureFlagUserTable,
} from "features/internal/feature-flags/InternalFeatureFlagEditPage";
import { useFeatureFlagRetrieveSuspense } from "gen/endpoints/feature-flags/feature-flags";
import { FeatureFlagReadData } from "gen/model/featureFlagReadData";
import { FeatureFlagTypeEnum } from "gen/model/featureFlagTypeEnum";
import { TFunction } from "i18next";
import { get } from "lodash-es";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
  ContentBlockHeader,
  ContentBlockTitle,
} from "shared/components/content/ContentBlock";
import Descriptions from "shared/components/descriptions/Descriptions";
import PageBody from "shared/components/layout/PageBody";
import PageContent from "shared/components/layout/PageContent";
import { PageHeader } from "shared/components/layout/PageHeader";
import { RowSelectionState } from "shared/components/table/data/DataTableTypes";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { rootRoute } from "shared/navigation/Routes";
import { naiveFormatEnumValue } from "shared/utils/formattingUtils";

const getFeatureFlagScope = (
  featureFlagData: FeatureFlagReadData | undefined,
  t: TFunction<["common", "internal"]>,
) => {
  if (!featureFlagData?.enabled) {
    return "";
  }

  switch (featureFlagData.flag_type) {
    case FeatureFlagTypeEnum.environment:
      return t("internal:featureFlagPage.everyone");
    case FeatureFlagTypeEnum.override:
      return t("internal:featureFlagPage.custom");
    default:
      return "";
  }
};

export const InternalFeatureFlagPage: React.FC = () => {
  const { t } = useTranslation(["common", "internal"]);
  const { featureFlagId } = useRequiredStringParams();
  const { data: featureFlagData } =
    useFeatureFlagRetrieveSuspense(featureFlagId);
  const navigate = useNavigate();

  const featureFlagName = naiveFormatEnumValue(
    get(featureFlagData, "name", ""),
  );

  const scope = getFeatureFlagScope(featureFlagData, t);

  const customerIds = useMemo(
    () =>
      featureFlagData.customer_ids
        ? Object.fromEntries(
            featureFlagData.customer_ids.map((customer_id) => [
              customer_id,
              true,
            ]),
          )
        : {},
    [featureFlagData],
  );
  const userIds = useMemo(
    () =>
      featureFlagData.user_ids
        ? Object.fromEntries(
            featureFlagData.user_ids.map((user_id) => [user_id, true]),
          )
        : {},
    [featureFlagData],
  );

  const [userRowSelection] = useState<RowSelectionState>(userIds);
  const [customerRowSelection] = useState<RowSelectionState>(customerIds);
  return (
    <PageContent>
      <PageHeader
        title={featureFlagName}
        badge={{
          color: featureFlagData.enabled ? "success" : "error",
          text: featureFlagData.enabled
            ? t("common:enabled")
            : t("common:disabled"),
        }}
        backButton={{
          title: t("common:back"),
          path: rootRoute({}).internal({}).featureFlags({}).$,
        }}
        helperText={t("internal:featureFlagPage.helperText")}
        actions={
          <Button
            colorScheme="secondary"
            onClick={() =>
              navigate(
                rootRoute({})
                  .internal({})
                  .featureFlag({ featureFlagId })
                  .edit({}).$,
              )
            }
          >
            {t("common:edit")}
          </Button>
        }
      />
      <PageBody>
        <ContentBlockGroup>
          <ContentBlock>
            <ContentBlockHeader>
              <ContentBlockTitle>{t("common:details")}</ContentBlockTitle>
            </ContentBlockHeader>
            <ContentBlockBody variant="descriptions">
              <Descriptions>
                <Descriptions.Item label={t("common:name")}>
                  {featureFlagName}
                </Descriptions.Item>
                <Descriptions.Item label={t("common:enabled")}>
                  {featureFlagData.enabled}
                </Descriptions.Item>
                {scope && (
                  <Descriptions.Item
                    label={t("internal:featureFlagPage.scope")}
                  >
                    {scope}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </ContentBlockBody>
          </ContentBlock>
        </ContentBlockGroup>
        {featureFlagData.flag_type == FeatureFlagTypeEnum.override && (
          <>
            <InternalFeatureFlagCustomerTable
              featureFlagId={featureFlagId}
              rowSelection={customerRowSelection}
            />
            <InternalFeatureFlagUserTable
              featureFlagId={featureFlagId}
              rowSelection={userRowSelection}
            />
          </>
        )}
      </PageBody>
    </PageContent>
  );
};
